"use strict";


import {EventsMgr, IEvents} from "/@lib@/commons/events.js";
import {Door, ErrorMsg, FactoryRegistry, House, IDoor, IDoorBase, InitSlaveRep, InitSlaveReq, IPublicDoor, IReply, IRequest, Msg, ODoorConfig, OLocalityConfig, ReplyMsg, ResetStatesMsg, RoadEnd, State} from "/@lib@/edit/ot/urban.js";

/**
 * Extension du framework {house} pour gérer un système d'Operationnal Transformation.
 */
interface OHouseOtConfig extends OLocalityConfig {

	/** "async" (default),  "sync" (pour debug ou pour masterRoot sans ui: ie sans intérêt d'un edenHist). */
	otEngine?: "async" | "sync";

	/** Nombre d'entrées minimale dans l'historique de la house. 100 par défaut. */
	historyMinEntries?: number;

	/** Nombre d'entrées maximale dans l'historique de la house. historyMinEntries + 50 par défaut. */
	historyMaxEntries?: number;

	/**
	 * Pour otEngine="async", en ms délai max avant constitution d'une entrée d'historique. 500ms par défaut.
	 * Mettre -1 pour désactiver la fonction de merge.
	 * Mettre 1 en tests unitaire (pas 0, car peut-être très long en fonction du navigateur en 1er plan ou pas...)
	 */
	mergeEditMaxInterval?: number;
}

/** Events "classiques" dans le contexte JS non diffusés via les roads inter-houses. */
export interface IHouseOtEvents extends IEvents {
	/** Etat dirty par rapport au dernier enregistrement persistant. */
	dirtyChange: (dirty: boolean) => void;
}

/**
 * House de type Operationnal Transformation pour offrir des environnements d'édition concurrents.
 */
abstract class HouseOt extends House {

	/** Id pour différencier chaque slave et le master. */
	slaveId: number = 0;

	/** Implémentation de l'Engine OT en mode synchrone ou asynchrone. */
	otEngine: IOtEngine;

	protected _listeners: EventsMgr<IHouseOtEvents>;
	get listeners(): EventsMgr<IHouseOtEvents> {return this._listeners || (this._listeners = new EventsMgr())}

	constructor(options: OHouseOtConfig = {}) {
		super(options);
		this.initHouse(options);
	}

	protected initHouse(options: OHouseOtConfig) {
		if (options.otEngine === "sync") {
			this.otEngine = new OtEngineSync(this, options);
		} else {
			this.otEngine = new OtEngineAsync(this, options);
		}
	}

	/**
	 *
	 * @param roadEnd
	 * @param doorConfig Config de de la door. cf params dans Door.openDoor()
	 * @returns {Door}
	 */
	openDoor(roadEnd: RoadEnd, doorConfig: ODoorConfig = {}): Door {
		const isOt = doorConfig.isMaster || doorConfig.roadToMaster;
		return this.xDeclareDoor(new (isOt ? DoorOt : Door)(this, ++this._doorCounter, roadEnd, doorConfig), doorConfig)
	}

	isDirty: boolean = false;

	setDirty(dirty: boolean) {
		if (this.isDirty !== dirty) {
			this.isDirty = dirty;
			if (this._listeners) this._listeners.emit('dirtyChange', dirty);
		}
	}

	/**
	 * @return true, false ou "soon".
	 */
	isUndoAvailable(filter?: IMsgFilter): boolean | OUndoRedoSoon {
		const undoed = this.xNextUndoed(filter || msgFromPublicDoor);
		if (undoed === HouseOt.SOON) return HouseOt.SOON;
		return undoed !== null;
	}

	protected xNextUndoed(filter: IMsgFilter): MsgOt | OUndoRedoSoon {
		let previousOtId: OtId = null;
		let msg;
		let invert;
		do {
			do {
				msg = this.otEngine.nextUndoable(previousOtId, filter);
				if (!msg) return null;
				if (!this.otEngine.isMsgStable(msg)) return HouseOt.SOON;
				previousOtId = msg.otId;
			} while (msg.killed);
			invert = this.otEngine.invertMsg(msg, 'undo');
		} while (invert.killed);
		if (HouseOt.ASSERT) this.assertInvertedMsg(invert, msg, 'undo');
		return invert;
	}

	undo(fromDoor?: IPublicDoor, filter?: IMsgFilter): MsgOt | OUndoRedoSoon {
		if (HouseOt.DEBUG) console.group("START UNDO");
		const invert = this.xNextUndoed(filter || msgFromPublicDoor);
		if (!invert || invert === HouseOt.SOON) {
			if (HouseOt.DEBUG) console.log("CANCEL UNDO: " + invert);
			return invert;
		} else {
			invert.setMeta(MSGMETA_noCleanup, true);
			(fromDoor || this.publicDoor).receiveMsg(invert);
		}
		if (HouseOt.DEBUG) console.groupEnd();
		return invert;
	}

	willUndo(fromDoor?: IPublicDoor, filter?: IMsgFilter): Promise<boolean> {
		return new Promise((resolve, reject) => {
			try {
				if (this.otEngine.isHouseStabilized) {
					resolve(this.undo(fromDoor, filter || msgFromPublicDoor) != null);
				} else {
					this.otEngine.signalWhenStabilized(() => {
						resolve(this.undo(fromDoor, filter || msgFromPublicDoor) != null);
					})
				}
			} catch (e) {
				reject(e);
			}
		});
	}


	/**
	 * @return true, false ou "soon".
	 */
	isRedoAvailable(filter?: IMsgFilter): boolean | OUndoRedoSoon {
		const reoded = this.xNextRedoed(filter || msgFromPublicDoor);
		if (reoded === HouseOt.SOON) return HouseOt.SOON;
		return reoded !== null;
	}

	protected xNextRedoed(filter: IMsgFilter): MsgOt | OUndoRedoSoon {
		let previousOtId: OtId = null;
		let msg;
		let invert;
		do {
			do {
				msg = this.otEngine.nextRedoable(previousOtId, filter);
				if (!msg) return null;
				if (!this.otEngine.isMsgStable(msg)) return HouseOt.SOON;
				previousOtId = msg.otId;
			} while (msg.killed);
			invert = this.otEngine.invertMsg(msg, 'redo');
		} while (invert.killed);
		if (HouseOt.ASSERT) this.assertInvertedMsg(invert, msg, 'redo');
		return invert;
	}

	clearHistory() {
		this.otEngine.resetHistory(this.otEngine.sharedProgress);
	}

	assertInvertedMsg(invert: MsgOt, msgFrom: MsgOt, action: 'undo' | 'redo') {
		try {
			invert.assertMsgValidity(this);
		} catch (e) {
			console.log(e);
			const oldDebug = HouseOt.DEBUG;
			try {
				//On rejoue l'inversion en debug pour les logs
				HouseOt.DEBUG = true;
				this.otEngine.invertMsg(msgFrom, action);
			} finally {
				HouseOt.DEBUG = oldDebug
			}
			throw e;
		}
	}

	redo(fromDoor?: IPublicDoor, filter?: IMsgFilter): MsgOt | OUndoRedoSoon {
		if (HouseOt.DEBUG) console.group("START REDO");
		const invert = this.xNextRedoed(filter || msgFromPublicDoor);
		if (!invert || invert === HouseOt.SOON) {
			if (HouseOt.DEBUG) console.log("CANCEL REDO: " + invert);
		} else {
			invert.setMeta(MSGMETA_noCleanup, true);
			(fromDoor || this.publicDoor).receiveMsg(invert);
		}
		if (HouseOt.DEBUG) console.groupEnd();
		return invert;
	}

	willRedo(fromDoor?: IPublicDoor, filter?: IMsgFilter): Promise<boolean> {
		return new Promise((resolve, reject) => {
			try {
				if (this.otEngine.isHouseStabilized) {
					resolve(this.redo(fromDoor, filter || msgFromPublicDoor) != null);
				} else {
					this.otEngine.signalWhenStabilized(() => {
						resolve(this.redo(fromDoor, filter || msgFromPublicDoor) != null);
					})
				}
			} catch (e) {
				reject(e);
			}
		});
	}

	initFromMaster(cbInit?: (reply: InitSlaveRep) => Promise<this>): Promise<this> {
		this.otEngine.inited = false; //on bloque toute action tant que l'init n'a pas abouti.
		return this.masterDoor.sendRequest(this.xCreateInitSlaveReq()).then(async (rep: InitSlaveRep) => {
			if (cbInit) {
				const h = await cbInit(rep);
				if (h !== this) return h; //abandon, siwitch sur une autre house...
			}
			this.slaveId = rep.slaveId;
			this.otEngine.resetHistory(rep.datas.progress);
			this.xApplyResetStatesLocally(rep);
			//On dispatch cette nouvelle version partout. ?
			this.broadcastMsg(new ResetStatesMsg().init(rep.datas).setDoorId(this.masterDoor.id), this.masterDoor.id);
			return this;
		});
	}

	onMsg(msg: Msg, fromDoor: IDoorBase) {
		if (msg instanceof MsgOt && msg.isProgressSensitive) {
			this.otEngine.onMsgOt(msg, fromDoor);
			if (!this.isDirty && msg.isBodyMutator && msg.getMeta("corr") !== "init") this.setDirty(true);
			return;
		}
		switch (msg.type) {
		case InitSlaveReq.type : {
			this.otEngine.replyInitSlave(msg, fromDoor);
			return;
		}
		case ResetStatesMsg.type : {
			//utilisé uniquement dans le cas d'un slave de 2ème niveau, lorsque le slave de 1er niveau
			//a broadcasté le InitSlaveRep suite à un OutOfSynch.
			this.otEngine.resetHistory((msg as ResetStatesMsg).datas.progress);
			this.xApplyResetStatesLocally(msg as ResetStatesMsg);
			//On dispatch cette nouvelle version partout.
			this.broadcastMsg(msg, this.masterDoor.id);
			return;
		}
		case UndirtyMsgOt.type: {
			if (this.otEngine.isHouseStabilized && (msg as UndirtyMsgOt<any>).prg == this.otEngine.sharedProgress) {
				this.setDirty(false)
			}
			return;
		}
		}
		if (msg instanceof ErrorMsg) {
			//En cas d'erreur, on remonte l'info dans les publicDoors.
			this.broadcastMsgToPublicDoors(msg);
		}
	}

	async onRequest<R extends IReply>(msg: IRequest<R>, fromDoor: IPublicDoor): Promise<R> {
		switch (msg.type) {
		case AskForSaveReq.type: {
			if (!this.isMasterRoot()) {
				if (!this.otEngine.isHouseStabilized) await this.otEngine.waitForStabilized();
				return this.masterDoor.sendRequest(msg);
			}
		}
		}
	}

	/** Création d'un requête d'iintialisation d'une house slave. */
	xCreateInitSlaveReq(): InitSlaveReq {
		return new InitSlaveReq();
	}

	/**
	 * Traitement des Msg de type OT aux states locaux.
	 * Le msg peut être corrigé par un éventuel schéma (mutate, complete, mergeText, normChars) si:
	 * - on est issu de notre publicDoor
	 * - ou on est la house Master (résolution des conflits d'édition concurrents).
	 * Le msg ne doit plus être corrigé lorsqu'il remonte du master.
	 *
	 * Si le msg est "correctable", la suppr des balises inconnues/interdites peut être désactivée
	 * par une meta "noCleanup" (cf const MSGMETA_noCleanup) : contexte de user / éditeur avancé, vue xml...
	 * Mais cette meta n'est pas prise en compte si on est le master en édition collaborative (ie avec des houses slaves),
	 * car le cleanup est nécessaire pour la résolution de certains conflits d'édition (création simultannée d'une balise en card ?).
	 *
	 * @return le même msg ou le msg corrigé.
	 */
	abstract xApplyMsgOtLocally(msg: MsgOt, correctable: boolean, noCleanup?: boolean): MsgOt;

	/** Init / reset de la house slave (msg de type InitSlaveRep ou ResetStatesMsg). */
	xApplyResetStatesLocally(msg: InitSlaveRep | ResetStatesMsg): void {
		this.setDirty(false);
	}

	/**
	 * Indique qu'un undo ou redo est possible mais pas tout de suite (OtEngine asynchrone).
	 * Un msg qui est encore dans la transientList ne peut être "undoé" ou "redoé" car il peut être
	 * modifié lors de l'adjust sur le master. Le msg initial et son inverse divergent alors et l'algo
	 * de mémorisation des insertions/suppressions imbriquées pour le "cross" des inversions suivantes échoue.
	 *
	 * @see HouseOt#isUndoAvailable()
	 * @see HouseOt#isRedoAvailable()
	 */
	static readonly SOON: OUndoRedoSoon = 'soon';
}

type OUndoRedoSoon = 'soon';

/** Filtre des msg pour undo/redo. */
type IMsgFilter = (m: Msg) => boolean;

const msgFromPublicDoor = function (m: Msg) {
	return m.doorId < 0;
} as IMsgFilter;


/** Porte d'une house permettant une connexion vers une porte d'une autre house via une route (road). */
class DoorOt extends Door {
	protected _otEngine: IOtEngine;

	constructor(house: HouseOt, doorId: number, roadEnd: RoadEnd, configDoor: ODoorConfig) {
		super(house, doorId, roadEnd, configDoor);
		this._otEngine = house.otEngine;
	}

	/** Envoi un message simple. */
	sendMsg(msg: Msg) {
		if ((msg as MsgOt).isProgressSensitive) (msg as MsgOt).prg = this._otEngine.sharedProgress;
		this.roadEnd.pushMsg(msg);
	}

	/** Envoie une request. Un Promise[resolve(Reply), reject(Reply)] est retourné. */
	sendRequest<R extends IReply>(msg: IRequest<R>): Promise<R> {
		if ((msg as MsgOt).isProgressSensitive) (msg as MsgOt).prg = this._otEngine.sharedProgress;
		return this.roadEnd.pushRequest(msg);
	}

	receiveMsg(msg: Msg) {
		if (House.DEBUG) console.group(`ReceiveMsg in ${this.house} : ${msg}`);
		msg.setDoorId(this.id);
		if ((msg as MsgOt).isProgressSensitive) this._otEngine.adjustOrForwardForeignMsg(msg as MsgOt, this);
		this.house.onMsg(msg, this);
		if (House.DEBUG) console.groupEnd();
	}

}

interface IOtEngine {
	house: HouseOt
	inited: boolean
	sharedProgress: number

	getMsgByOtId(otId: OtId): MsgOt

	onMsgOt(msg: MsgOt, fromDoor: IDoorBase): void

	nextUndoable(beforeOtId: OtId, filter: IMsgFilter): MsgOt

	nextRedoable(beforeOtId: OtId, filter: IMsgFilter): MsgOt

	invertMsg(msg: MsgOt, action: 'undo' | 'redo'): MsgOt

	isMsgStable(msg: MsgOt): boolean

	replyInitSlave(initSlaveReq: InitSlaveReq, fromDoor: IDoorBase): void

	resetHistory(startProgress: number): void

	isMsgStable(msg: MsgOt): boolean

	/** Appelé par la DoorOt pour un message entrant extérieur. */
	adjustOrForwardForeignMsg(msg: MsgOt, fromDoor: IDoor): void

	/**
	 * La house est-elle stabilisée, ie plus de Msg en cours de traitement non encore retournés par le master.
	 */
	isHouseStabilized: boolean

	/**
	 * Appelle une callback en asynchrone lorsque le contenu de cette house est stabilisé.
	 * Attention, appel asynchrone, donc pas impossible qu'un traitement intermédiaire
	 * rende entre temps le contenu désynchronisé avec le master.
	 *
	 * Pratique pour tests unitaires.
	 */
	signalWhenStabilized(callback: () => void): void

	/** Version Promise de signalWhenStabilized(cb). */
	waitForStabilized(): Promise<void>
}

/**
 * Implémentation master/slave synchrone : la modification effective sur le slave
 * n'est effectuée qu'après intégration du Msg dans le master root.
 *
 * Engine à utiliser uniquement sur la house masterRoot, sans usage de ses publicDoors
 * (pas de edenHist pour le merge des msg), ou alors en debug avant impl des forward/rewind sur les msg.
 */
class OtEngineSync implements IOtEngine {

	startProgress = 0;
	house: HouseOt;
	inited: boolean;

	_sharedHist: Array<MsgOt | true> = [];
	protected _histMinEntries: number;
	protected _histMaxEntries: number;

	constructor(house: HouseOt, options: OHouseOtConfig) {
		this.house = house;
		this._histMinEntries = options.historyMinEntries || 100;
		this._histMaxEntries = options.historyMaxEntries || (this._histMinEntries + 50);
		this.inited = true;
	}

	/**
	 * Indicateur de progression partagé par toutes les houses synchronisées.
	 * A l'initialisation, la progression est 0.
	 *
	 * La progression associée à un Msg (Msg.prg) est le sharedProgress avant son insertion dans l'historique.
	 *
	 */
	get sharedProgress(): number {
		return this._sharedHist.length;
	}

	getMsgByOtId(otId: OtId): MsgOt {
		return this._sharedHist[otId as number] as MsgOt;
	}

	onMsgOt(msg: MsgOt, fromDoor: IDoor) {
		if (!this.inited) return; //tant que la house n'a pas été initialisée, on abandonne les events Ot.
		if (this.house.isMasterRoot()) {
			//on est le master root
			try {
				msg = this.house.xApplyMsgOtLocally(msg, true, fromDoor.isPublic() ? msg.getMeta(MSGMETA_noCleanup) : false);
				this.historicizeMsg(msg);
			} catch (e) {
				this.house.onInternalError(e);
				return;
			}
			this.house.broadcastMsg(msg);
		} else if (fromDoor.isPublic()) {
			//msg issu d'une de nos publicDoor, en mode synchro, on transfert sur notre master
			this.house.masterDoor.sendMsg(msg);
		} else if (fromDoor.isDoorToMaster) {
			//update imposé par notre master
			try {
				this.house.xApplyMsgOtLocally(msg, false);
				this.historicizeMsg(msg);
			} catch (e) {
				this.house.onInternalError(e);
				return;
			}
			this.house.broadcastMsg(msg, fromDoor.id);
		} else if (fromDoor.isDoorToSlave) {
			//on fait simple proxy d'un msg issu d'un de nos slaves, on transfert directement sur le master
			//la door dont il provient a déjà ajusté ce msg pour être en phase avec notre progress
			this.house.masterDoor.sendMsg(msg);
		}
	}

	/**
	 * Ajoute un msg isBodyMutator à l'historique de la house.
	 * Tout message historisé incrémente la propriété progress de la house.
	 */
	historicizeMsg(msg: MsgOt) {
		if (!msg.isBodyMutator) return;
		//On historise un clone pour éliminer toutes les propriétés transient,
		//le msg serveant de véhicule d'informations dans les couches UI.
		//PERF désactiver le clonage par config si house utilisée en mode serveur de synchro par ex. ?
		//let histMsg = msg.clone('hist');
		//histMsg.otId =
		msg.otId = this._sharedHist.length; //affectation du otId définitif (nécessaire pour undo/redo).
		this._sharedHist.push(msg);
		if (this._sharedHist.length - this.startProgress > this._histMaxEntries) {
			//discard too olds entries
			const newOldestProgress = this._sharedHist.length - this._histMinEntries;
			for (let i = this.startProgress; i < newOldestProgress; i++) {
				delete this._sharedHist[i];
			}
			this.startProgress = newOldestProgress;
		}
	}

	/** Gère l'inversion et l'ajustement d'un msg pour un undo/redo. */
	invertMsg(msg: MsgOt, action: 'undo' | 'redo'): MsgOt {
		const invert = msg.invert();
		invert.inMigration(true);
		invert.setMeta(action, msg.otId);
		if (HouseOt.DEBUG) console.log(action + ": invert : " + msg + "  ->  " + invert);
		this.xAdjustMsgForUndoRedo(msg, invert);
		invert.inMigration(false);
		return invert;
	}

	xAdjustMsgForUndoRedo(msg: MsgOt, invert: MsgOt) {
		if (HouseOt.ASSERT) if (this._sharedHist[msg.otId as number] !== msg) throw Error("this._sharedHist[msg.otId] !== msg!!!");
		for (let i = (msg.otId as number) + 1, s = this._sharedHist.length; i < s; i++) {
			const entry = this._sharedHist[i] as MsgOt;
			if (entry.slvId === this.house.slaveId) {
				if (entry.metas) {
					entry.cross(invert, entry.metas.redo /* redoOf en 1er car undoOf pourrait être = à 0.*/ || entry.metas.undo);
				} else {
					entry.cross(invert, null);
				}
				if (HouseOt.DEBUG) console.log(`Cross inverted via sharedHist : ${entry} -> ${invert}`);
			} else {
				//msg d'une autre house.
				entry.adjust(invert);
				if (HouseOt.DEBUG) console.log(`Adjust inverted via sharedHist : ${entry}  ->  ${invert}`);
			}
		}
	}

	adjustOrForwardForeignMsg(msg: MsgOt, fromDoor: IDoor) {
		if (msg.prg < this.startProgress) throw ErrorMsg.OUT_OF_SYNC;
		const houseProgress = this.sharedProgress;
		if (msg.prg < houseProgress) {
			msg.inMigration(true);
			for (let i = msg.prg; i < houseProgress; i++) {
				const histoMsg = this._sharedHist[i] as MsgOt;
				if (histoMsg.doorId !== fromDoor.id) {
					histoMsg.adjust(msg);
					if (HouseOt.DEBUG) console.log("Adjusted via " + histoMsg + " --> " + msg);
				} else {
					histoMsg.forward(msg);
					if (HouseOt.DEBUG) console.log("Forwarded via " + histoMsg + " --> " + msg);
				}
			}
		}
		msg.inMigration(false); //XXX pourquoi pas au même niveau que msg.inMigration(true) ?
	}

	replyInitSlave(initSlaveReq: InitSlaveReq, fromDoor: IDoorBase) {
		const datas = Object.create(null);
		this.house.writeFullStates(datas, initSlaveReq.metas);
		datas.progress = this.sharedProgress;
		fromDoor.sendMsg(new InitSlaveRep().init(initSlaveReq, this.house.newSlaveId(), datas));
	}

	/**
	 * Reset l'historique suite à un outOfSync avec le master.
	 * Les entrées précédentes ne peuvent plus être utilisés (même pour un undo/redo local)
	 * avec le nouveau contenu de la house qui a été récupéré.
	 */
	resetHistory(startProgress: number) {
		this.startProgress = startProgress;
		this._sharedHist = [];
		if (startProgress > 0) this._sharedHist[startProgress - 1] = true; //pour que this._sharedHist.length == le bon this.sharedProgress
		this.inited = true;
	}

	nextUndoable(beforeOtId: OtId, filter: IMsgFilter): MsgOt {
		const it = this.newHistIterator();
		let msg = beforeOtId != null ? it.gotoProgress(beforeOtId).gotoPrevious().getMsg() : it.gotoLast().getMsg();
		while (msg && msg.isUndoable) {
			if (filter(msg)) {
				//Ce msg est bien undoabable dans notre contexte.
				if (msg.metas && 'undo' in msg.metas) it.gotoProgress(msg.metas.undo);
				else return msg;
			}
			//msg exclus, on remonte.
			msg = it.gotoPrevious().getMsg();
		}
		return null;
	}

	nextRedoable(beforeOtId: OtId, filter: IMsgFilter): MsgOt {
		const it = this.newHistIterator();
		let msg = beforeOtId != null ? it.gotoProgress(beforeOtId).gotoPrevious().getMsg() : it.gotoLast().getMsg();
		while (msg && msg.isUndoable) {
			//Ce msg est bien undoable/redoable
			if (filter(msg)) {
				//il vient de chez nous
				if (msg.metas) {
					if ('redo' in msg.metas) {
						//on remonte la chaine des redo pour trouver le précédent undo.
						it.gotoProgress(msg.metas.redo);
						msg = it.gotoPrevious().getMsg();
						continue;
					} else if ('undo' in msg.metas) {
						//on a trouvé le undo à "redoer".
						return msg;
					}
				}
				//autre msg de chez nous qui casse la chaine des redo, pas de redo possible.
				return null;
			}
			//msg exclus, on remonte.
			msg = it.gotoPrevious().getMsg();
		}
		return null;
	}

	newHistIterator(): IHistIterator {
		return new HistIteratorSync(this);
	}

	/** Msg stable = msg passé dans la sharedHist (pour l'engine Async). */
	isMsgStable(msg: MsgOt): boolean {
		return true
	}

	get isHouseStabilized() {
		return true;
	}

	signalWhenStabilized(callback: () => void): void {
		Promise.resolve().then(callback);
	}

	waitForStabilized(): Promise<void> {
		return Promise.resolve();
	}
}


interface IHistIterator {
	gotoLast(): IHistIterator;

	gotoPrevious(): IHistIterator;

	gotoProgress(progress: OtId): IHistIterator;

	getMsg(): MsgOt;
}

class HistIteratorSync implements IHistIterator {
	protected _first: number;
	protected _sharedHist: Array<MsgOt | true>;
	protected _offset: number;

	constructor(otEngine: OtEngineSync) {
		this._first = otEngine.startProgress;
		this._sharedHist = otEngine._sharedHist;
		this._offset = -1;
	}

	gotoLast() {
		this._offset = this._sharedHist.length - 1;
		return this;
	}

	gotoPrevious() {
		this._offset--;
		return this;
	}

	gotoProgress(progress: number) {
		this._offset = progress;
		return this;
	}

	getMsg(): MsgOt {
		if (this._offset < this._first || this._offset >= this._sharedHist.length) return null;
		return this._sharedHist[this._offset] as MsgOt;
	}
}


/**
 * Implémentation master/slave asynchrone : la modification sur le slave est immédiate.
 * Ces modifications executées en avance de phase par rapport au master sont isolés dans une pile transitoire.
 * Cette pile transitoire est dépilée au fur et à mesure que le master nous indique avoir intégré ces modifications
 * qui sont alors ajoutés dans l'historique définitif.
 */
class OtEngineAsync extends OtEngineSync {

	_transientHist: TransientHist;

	constructor(house: HouseOt, options: OHouseOtConfig) {
		super(house, options);
		this._transientHist = new TransientHist(this, options);
	}

	/**
	 * otId :
	 *   - 0 à N : index du msg dans la sharedHist.
	 *   - de -1 à -N == -1 - index du msg dans le tableau des transientHist
	 *   - "e" = Msg eden
	 *
	 */
	getMsgByOtId(otId: OtId) {
		const res = typeof otId === "number" && otId >= 0 ? this._sharedHist[otId] as MsgOt : this._transientHist.getMsg(otId);
		if (HouseOt.ASSERT) if (res && res.otId !== otId) throw Error(`getMsgByOtId(): ${res.otId} != ${otId}`);
		return res;
	}


	onMsgOt(msg: MsgOt, fromDoor: IDoorBase) {
		if (!this.inited) return; //tant que la house n'a pas été initialisée, on abandonne les events Ot.
		if (fromDoor.isPublic()) {
			try {
				//Modif locale, on applique localement de suite
				msg = this.house.xApplyMsgOtLocally(msg, true, msg.getMeta(MSGMETA_noCleanup));
				//on historise dans les encours
				this._transientHist.appendMsg(msg);
			} catch (e) {
				this.house.onInternalError(e);
				return;
			}
			//on le renvoie sur nos publicDoors de suite.
			this.house.broadcastMsgToPublicDoors(msg);
		} else if (fromDoor.isDoorToMaster) {
			//update imposé par le master
			if (msg.slvId == this.house.slaveId) {
				//Ce Msg est issu de notre house, il doit correspondre au 1er msg de la pile des encours.
				try {
					//FIXME appliquer les corrections ajoutées par le schéma sur le master.
					this._transientHist.transferMsgToSharedHist(); //on le purge des entrées transitoires et on le place dans le sharedHist.
				} catch (e) {
					this.house.onInternalError(e);
					return;
				}
			} else {
				//Ce Msg ne vient pas de notre house.
				//On ajuste le msg par rapport aux modifs transitoires pour pouvoir l'exécuter
				//On ajuste chaque msg transitoire avec msgToApply car il va se placer avant dans l'historique final.
				let msgToApply;
				try {
					msgToApply = this._transientHist.catchupWithForeignMsg(msg);
					//On execute localement.
					this.house.xApplyMsgOtLocally(msgToApply, false);
					//On historise dans l'historique définitif.
					this.historicizeMsg(msg);
				} catch (e) {
					this.house.onInternalError(e);
					return;
				}
				this.house.broadcastMsgToPublicDoors(msgToApply);
				//TODO broadcast vers nos propres slaves...
			}
			this.house.broadcastMsg(msg, fromDoor.id, Door.FILTER_NOT_PUBLIC); //on dispatch aux autres houses.
		} else if (fromDoor.isDoorToSlave) {
			//msg provenant d'une house slave
			if (this.house.isMasterRoot()) {
				//on est le master root
				try {
					msg = this.house.xApplyMsgOtLocally(msg, true);
					this.historicizeMsg(msg);
				} catch (e) {
					this.house.onInternalError(e);
					return;
				}
				this.house.broadcastMsg(msg);
			} else {
				throw Error("TODO proxy house intermédiaire à revoir...");
				//on fait simple proxy d'un msg issu d'un de nos slaves, on transfert directement sur le master
				//la door dont il provient a déjà ajusté ce msg pour être en phase avec notre progress du master
				//this.house.masterDoor.sendMsg(msg);
			}
		} else {
			console.trace("Unknown msg coming from !!");
		}
	}

	/** Gère l'inversion et l'ajustement d'un msg pour un undo/redo. */
	invertMsg(msg: MsgOt, action: 'undo' | 'redo'): MsgOt {
		//une action de undo/redo impose de finaliser le msg eden pour bloquer la fusion avec ce undo/redo
		// et pour éviter d'avoir msg.otId=="e" mémorisé dans Msg.metas.undo/redo non transformable en progression définitive
		this._transientHist.finalizeEdenMsg();
		const invert = msg.invert();
		invert.inMigration(true);
		invert.setMeta(action, msg.otId);
		if (HouseOt.DEBUG) console.log(action + ": invert : " + msg + "  ->  " + invert);
		if (msg.otId as number >= 0) {
			//on remonte la sharedHist.
			this.xAdjustMsgForUndoRedo(msg, invert);
			//on remonte la pile de transient
			this._transientHist.adjustMsgForUndoRedo(invert, 0);
		} else {
			this._transientHist.adjustMsgForUndoRedo(invert, msg.otId);
		}
		invert.inMigration(false);
		return invert;
	}

	// Tant qu'on ne souhaite pas qu'une house master intermédiaire ne traite les msg de ses slaves
	// mais ne fait que les proxier vers le masterRoot, on n'ajuste pas msg avec les transient et eden.
	//adjustOrForwardForeignMsg(msg, fromDoor) {
	//	super.adjustOrForwardForeignMsg(msg, fromDoor);
	//}

	replyInitSlave(initSlaveReq: InitSlaveReq, fromDoor: IDoorBase) {
		if (this._transientHist.isSync()) {
			super.replyInitSlave(initSlaveReq, fromDoor);
		} else {
			//On attend que les transient msg soient traités pour avoir la sharedProgress et les states de la house en phase et répondre au slave.
			this._transientHist.addSignalInSync(() => {
				super.replyInitSlave(initSlaveReq, fromDoor);
			})
		}
	}

	newHistIterator(): IHistIterator {
		return new HistIteratorAsync(this);
	}

	/** Msg stable = msg passé dans la sharedHist (pour l'engine Async). */
	isMsgStable(msg: MsgOt) {
		return msg.otId as number >= 0;
	}

	get isHouseStabilized() {
		return this._transientHist.isSync();
	}

	/**
	 * Appelle une callback en asynchrone lorsque le contenu de cette house est stabilisé.
	 * Attention, appel asynchrone, donc pas impossible qu'un traitement intermédiaire
	 * rende entre temps le contenu désynchronisé avec le master.
	 *
	 * Pratique pour tests unitaires.
	 */
	signalWhenStabilized(callback: () => void): void {
		if (this._transientHist.isSync()) {
			Promise.resolve().then(callback)
		} else {
			this._transientHist.addSignalInSync(callback)
		}
	}

	waitForStabilized(): Promise<void> {
		if (this._transientHist.isSync()) {
			return Promise.resolve();
		} else {
			return new Promise<void>(resolve => {
				this._transientHist.addSignalInSync(resolve);
			});
		}
	}

}


/**
 * Gestion de la pile des MsgOt transitoires, à savoir les msg pris en compte localement dans cette house slave,
 * mais pas encore intégrés par le master dans la sharedHist.
 * Cet objet gère aussi le dernier message "eden" qui n'est même pas encore envoyé au master car susceptible d'être
 * fusionné avec les prochains messages (agrégation du texte saisie).
 */
class TransientHist {

	static readonly OTID_EDEN = 'e';

	house: HouseOt;
	mergeEditMaxInterval: number;

	/** Pile des msg transitoires. */
	_hist: Array<MsgOt | OtId> = [];

	/**
	 * 1ere entrée de la pile des msg transitoires.
	 * les offset < this._firstActive gardent la mémoire des sharedProgress "définitifs" pour la gestion des undo/redo.
	 */
	_firstActive = 0;

	/**
	 * Msg rewindés envoyés au master en attente de retour du master.
	 * this._pendings[X] est un clone rewindé de this._hist[X].
	 */
	_pendings: Array<MsgOt> = [];

	/** Message en émergence susceptible d'être fusionné avec les prochains msg avant d'être envoyé au master.*/
	_eden: MsgOt;

	protected _finalizeThread: number;

	constructor(public engineOt: OtEngineAsync, options: OHouseOtConfig) {
		this.engineOt = engineOt;
		this.house = engineOt.house;
		this.mergeEditMaxInterval = options.mergeEditMaxInterval || 700;
	}

	isSync() {
		return !this._eden && this._firstActive === this._hist.length;
	}

	/**
	 *
	 * @param otId "e" ou -1 à -N.
	 */
	getMsg(otId: OtId): MsgOt {
		if (otId === TransientHist.OTID_EDEN) return this._eden;
		return this._hist[-1 - (otId as number)] as MsgOt;
	}

	/** On ajoute un message issu de notre publicDoor à la pile des transient. */
	appendMsg(msg: MsgOt) {
		if (this._eden) {
			const mergeMsg = this._eden.mergeWith(msg);
			if (mergeMsg) {
				this._eden = mergeMsg;
			} else {
				this.finalizeEdenMsg();
				this._eden = msg;
			}
		} else {
			this._eden = msg;
		}
		if (this._finalizeThread !== null) clearTimeout(this._finalizeThread);
		if (this.mergeEditMaxInterval < 0 || !TransientHist.isMergeableMsg(this._eden)) {
			this.finalizeEdenMsg();
		} else {
			this._eden.otId = TransientHist.OTID_EDEN;
			this._finalizeThread = window.setTimeout(() => {
				this.finalizeEdenMsg()
			}, this.mergeEditMaxInterval);
		}
	}

	static isMergeableMsg(msg: MsgOt) {
		if (msg.metas && (msg.metas.undo != null || msg.metas.redo != null)) return false;
		return msg.isMergeable || false;
	}

	/** Transfert le msg eden vers le transientHist ou le sharedHist si la house est masterRoot. */
	finalizeEdenMsg() {
		const msg = this._eden;
		if (!msg) return;
		this._eden = null;
		if (this.house.isMasterRoot()) {
			//On est le masterRoot, donc on court-cicuite les transient
			this.engineOt.historicizeMsg(msg);
			//et on dispatch partout sauf les publicDoors (msg déjà reçu)
			this.house.broadcastMsg(msg, 0, Door.FILTER_NOT_PUBLIC);
			//plus d'eden, donc on est inSync.
			this.xSignalInSync();
		} else {
			const offset = this._hist.length;
			this._hist.push(msg);
			msg.otId = -this._hist.length;
			//on envoie sur le master le msg rembobiné au sharedProgress.
			const msgToSend = msg.clone();
			if (HouseOt.DEBUG) console.group(`Send to master from ${this.engineOt.house} ${msgToSend}`);
			if (offset > this._firstActive) {
				//on rewind le msg jusqu'à la sharedProgress pour pouvoir l'envoyer à la master house.
				msgToSend.inMigration(true);
				for (let i = offset - 1; i >= this._firstActive; i--) {
					//on remonte la pile des encours pour que msgToSend soit en phase avec le progress du master (sharedProgress).
					(this._hist[i] as MsgOt).rewind(msgToSend);
					if (HouseOt.DEBUG) console.log(`Rewinded via transient ${this._hist[i]} -> ${msgToSend}`);
				}
			}
			if (HouseOt.DEBUG) console.groupEnd();
			this._pendings.push(msgToSend);
			this.house.masterDoor.sendMsg(msgToSend);
		}
	}

	/** Transfert le 1er msg de la pile transient vers le sharedHist. */
	transferMsgToSharedHist() {
		const msgToMove = this._hist[this._firstActive] as MsgOt;
		if (msgToMove.metas) {
			//Si ce msg était un undo/redo d'un msg transitoire à l'époque, on mémorise sa ref définitive
			if (msgToMove.metas.undo < 0) msgToMove.metas.undo = this._hist[-1 - msgToMove.metas.undo];
			if (msgToMove.metas.redo < 0) msgToMove.metas.redo = this._hist[-1 - msgToMove.metas.redo];
		}
		//on le place dans l'historique définitif.
		this.engineOt.historicizeMsg(msgToMove);
		if (this._firstActive + 1 == this._hist.length) {
			//plus d'entrées, inutile de préserver le sharedProgress des précédents msg transitoires.
			//reset du tableau des transitoires.
			this._hist.length = 0;
			this._pendings.length = 0;
			this._firstActive = 0;
			if (!this._eden) this.xSignalInSync();
		} else {
			//On supprime ce msg de l'hist
			//On mémorise le prg définitif de ce msg transitoire au cas où il aurait été référencé (undo/redo)
			//par un msg suivant dans la pile des transitoires (à la place du msg lui-même).
			this._hist[this._firstActive] = msgToMove.otId;
			//Gestion des pendings : on forward les msg pending suivants via celui qu'on supprime
			const pendingToRemove = this._pendings[this._firstActive];
			pendingToRemove.inMigration(false);
			for (let i = this._firstActive + 1; i < this._pendings.length; i++) {
				if (HouseOt.DEBUG) console.group(`Wil forward pending via ${pendingToRemove} : ${this._pendings[i]}`);
				pendingToRemove.forward(this._pendings[i]);
				if (HouseOt.DEBUG) console.log(`Pending forwarded : ${this._pendings[i]}`);
				if (HouseOt.DEBUG) console.groupEnd();
			}
			this._pendings[this._firstActive] = null;
			//On décale le début de notre pile transient.
			this._firstActive++;
		}
	}

	/**
	 * Double ajustement de la pile des transitoires [1] et de ce msg (issu du master) [2] pour :
	 * [1] adapter la pile des transitoires à ce nouveau msg injecté en amont dans la sharedHist
	 * et [2] pouvoir appliquer msg à l'état courant des states de cette house.
	 */
	catchupWithForeignMsg(msg: MsgOt) {
		if (this.isSync()) return msg;

		if (!msg.isBodyMutator) {
			if (msg.isProgressSensitive) {
				for (let i = this._firstActive; i < this._pendings.length; i++) {
					this._pendings[i].adjust(msg);
				}
				this._eden?.adjust(msg);
			}
			return msg;
		}

		//Pour simplifier l'injection des msg étrangers, on fige le msg eden.
		this.finalizeEdenMsg();

		if (HouseOt.DEBUG) console.group("Catching in " + this.engineOt.house + " for " + msg);

		//1 - Adjust de tous les pending msgs par le msg issu du master
		for (let i = this._firstActive; i < this._pendings.length; i++) {
			const pending = this._pendings[i];
			if (HouseOt.DEBUG) console.group("Will adjust pending " + pending);
			//Si this._pendings[i] était seul lors de son affectation dans finalizeEdenMsg(), il n'a pas
			// été placé en inMigration(true), on s'assure ici d'être dans ce mode.
			// Il sera mis à false dans transferMsgToSharedHist()
			pending.inMigration(true);
			msg.adjust(pending);
			if (HouseOt.DEBUG) console.log("Adjusted pending " + pending);
			if (HouseOt.DEBUG) console.groupEnd();
		}

		const pendingStack = [this._pendings[this._firstActive].clone()];
		pendingStack[0].inMigration(false); //reset du mode inMigr du clone.

		//2 - Forward des msg pendings suivants
		for (let i = this._firstActive + 1; i < this._pendings.length; i++) {
			const pending = this._pendings[i].clone();
			if (HouseOt.DEBUG) console.group(`Will forward pending ${pending}`);
			for (let k = 0; k < pendingStack.length; k++) {
				pendingStack[k].forward(pending);
				if (HouseOt.DEBUG) console.log(`Forwarded via ${pendingStack[k]} ->  ${pending}`);
			}
			pending.inMigration(false); //reset du mode inMigr du clone.
			if (HouseOt.DEBUG) console.groupEnd();
			pendingStack.push(pending);
		}

		//3 - Remplacement des nouveaux msgs transient et construction de la stack des transient inversés
		const transientInvertedStack = [];
		for (let i = pendingStack.length - 1, p = this._hist.length - 1; i >= 0; i--) {
			const mirrorMsg = this._hist[p] as MsgOt;
			//Affectation du nouveau transient dans this._hist.
			const msg = pendingStack[i];
			msg.otId = mirrorMsg.otId;
			this._hist[p--] = msg;
			//TODO eval cas simples où mirrorMsg et msg s'annulent et n'interfèrent pas avec les autres msgs. Si annulation possible : suppr dans pendingStack;
			//Pas d'annulation possible des 2 messages
			if (!mirrorMsg.killed) transientInvertedStack.push(mirrorMsg.invert());
		}
		if (HouseOt.DEBUG) console.log(`Transient inverted to apply ${transientInvertedStack}`);
		if (HouseOt.DEBUG) console.log(`Pending to apply ${pendingStack}`);
		if (HouseOt.DEBUG) console.groupEnd();

		return new ListMsgOt().initList(transientInvertedStack.concat(msg, pendingStack));
	}

	/** Ajuste un msg issu du passé (undo/redo) jusqu'à l'état actuel de la house. */
	adjustMsgForUndoRedo(msg: MsgOt, afterOtId: OtId) {
		if (HouseOt.ASSERT) if (afterOtId as number > 0) throw Error("afterOtId > 0 !!!");
		if (afterOtId === TransientHist.OTID_EDEN) return;
		for (let i = Math.max(this._firstActive, -afterOtId), s = this._hist.length; i < s; i++) {
			const entry = this._hist[i] as MsgOt;
			if (entry.metas) {
				entry.cross(msg, entry.metas.redo /* redoOf en 1er car undoOf pourrait être = à 0.*/ || entry.metas.undo);
			} else {
				entry.cross(msg, null);
			}
			if (HouseOt.DEBUG) console.log(`Cross inverted via transient : ${entry} -> + ${msg}`);
		}
		if (this._eden) throw Error("Eden msg should be finilized here. Or to do !");
	}

	/**
	 * Ajoute une function qui sera appelée 1 seule fois lorsque les states et le sharedProgess
	 * seront synchronisés (ie quand le transientHist sera vide).
	 */
	addSignalInSync(func: () => void) {
		if (!this._signalInSync) this._signalInSync = [];
		this._signalInSync.push(func);
	}

	protected _signalInSync: Array<() => void>;

	xSignalInSync() {
		const arr = this._signalInSync;
		if (arr == null) return;
		this._signalInSync = null;
		for (let i = 0, s = arr.length; i < s; i++) arr[i]();
	}
}

/**
 * Iterator enchainant eden, transient et shared.
 * Le cursor utilisé dans l'impl de cet iterator est la progression pour identifier la liste à interroger :
 * - >=0 : sharedHist[prg]
 * - <0 : transientHist[-1-prg]
 * - TransientHist.OTID_EDEN : eden.
 * - null : parcours achevé.
 *
 */
class HistIteratorAsync implements IHistIterator {

	protected _startSharedPrg: number;
	protected _transientHist: TransientHist;
	protected _sharedHist: Array<MsgOt | true>;
	protected _prg: OtId;

	constructor(otEngine: OtEngineAsync) {
		this._transientHist = otEngine._transientHist;
		this._startSharedPrg = otEngine.startProgress;
		this._sharedHist = otEngine._sharedHist;
		this._prg = null;
	}


	gotoLast() {
		if (this._transientHist._eden) this._prg = TransientHist.OTID_EDEN;
		else if (this._transientHist._hist.length > this._transientHist._firstActive) this._prg = -this._transientHist._hist.length;
		else this._prg = this._startSharedPrg < this._sharedHist.length ? this._sharedHist.length - 1 : null;
		return this;
	}

	gotoPrevious() {
		if (typeof this._prg === "number") {
			if (this._prg >= 0) {
				//on remonte dans la sharedHist
				this._prg = this._prg > this._startSharedPrg ? this._prg - 1 : null;
				return this;
			}
			if (this._prg < 0) {
				const offsetTransient = -1 - this._prg;
				if (this._transientHist._firstActive < offsetTransient) {
					this._prg = offsetTransient < this._transientHist._hist.length ? this._prg + 1 : null;
					return this;
				}
				this._prg = this._startSharedPrg < this._sharedHist.length ? this._sharedHist.length - 1 : null;
				return this;
			}
		} else if (this._prg === TransientHist.OTID_EDEN) {
			if (this._transientHist._hist.length > this._transientHist._firstActive) this._prg = -this._transientHist._hist.length;
			else this._prg = this._startSharedPrg < this._sharedHist.length ? this._sharedHist.length - 1 : null;
		}
		return this;
	}

	gotoProgress(progress: OtId) {
		this._prg = progress;
		return this;
	}

	getMsg(): MsgOt {
		if (typeof this._prg === "number") {
			if (this._prg >= 0) return this._sharedHist[this._prg] as MsgOt;
			if (this._prg < 0) {
				const offset = -1 - this._prg;
				return offset >= this._transientHist._firstActive ? this._transientHist._hist[offset] as MsgOt : null;
			}
		} else if (this._prg === TransientHist.OTID_EDEN) {
			return this._transientHist._eden;
		}
		return null;
	}
}


/**
 * L'otId d'un MsgOt est dans le cas général un number.
 * En async, il peut prendre la valeur 'e' lorsque le MsgOt est dans l'état "eden"
 * (avant envoi, en attente d'une potentielle fusion avec le msg suivant).
 * Dans le cas de sous-MsgOt dans un ListMsgOt, l'otId est une string mémorisant la hiérarchie des MsgOt
 * cf ListMsgOt.cross().
 */
type OtId = number | 'e' | string;


/**
 * Message OT entre un master et un slave intégrant la transmission
 * de la progression de l'émetteur du Msg.
 * Propriétés supplémentaires :
 * - prg : progression partagée courante de l'émetteur du Msg (donc avant son exécution).
 * - houseId : id unique de la house émétrice du message. Nécessaire pour qu'un MsgOt entrant
 *   puisse être ajusté via {Msg#adjust} ou {Msg#forward}.
 *
 */
abstract class MsgOt extends Msg {

	/** Progression OT */
	prg: number;

	/** House à l'origine de ce msg (parmi les houses slaves ou master synchronisées). */
	slvId: number;

	/**
	 * Retourne un id unique parmi tous les MsgOt de cette house.
	 * Propriété non sérialisée qui peut évoluer en fonction de la situation
	 * du MsgOt.
	 *
	 * @see {IOtEngine.getMsgByOtId}
	 */
	otId: OtId;

	/**
	 * Ce Msg est-il dépendant de l'avancement (progress) et nécessite-t-il d'être ajusté/reculé/avancé par les
	 * msgs de types isBodyMutator intervenus entre temps.
	 * @see {MsgOt.adjust};
	 */
	get isProgressSensitive() {return true}

	/**
	 * Ce Msg constitue-t-il une modification du contenu qui devra être intégrée dans l'historique pour ajuster/reculer/avancer
	 * les messages qui sont isProgressSensitive.
	 * Note : on pourrait ajouter isHistoricizable pour des msg qu'on souhaiterait faire rentrer dans la chaine d'historique
	 * mais qui ne sont pas nécessairement des bodyMutator (SetDurationMsg par ex.).
	 */
	get isBodyMutator() {return false}

	/**
	 * Est primaire un Msg qui ne peut plus être décomposé en un à N messages plus primitifs.
	 * @see decompose()
	 */
	get isPrimary() {return true}

	/**
	 * Décompose ce Msg en un ou plusieurs messages primaires.
	 */
	decompose(): this {return this}

	/**
	 * Ajuste msg par rapport aux modifications induites par this.
	 * L'ajustement est réalisé lorsque this n'est pas un prédécesseur naturel de msg issu de la même house
	 * @see Msg.forward
	 *
	 * Doit être implémenté si isBodyMutator==true.
	 */
	adjust(msg: MsgOt): void {}

	/**
	 * Transforme le message msg (intervenu après this) pour qu'il soit en phase avec la progression
	 * d'une autre house (master) qui n'a pas encore traité this.
	 *
	 * Doit être implémenté si isBodyMutator==true.
	 *
	 * @param msg message à transformer
	 */
	rewind(msg: MsgOt): void {}

	/**
	 * Transformation inverse de Msg.rewind(msg).
	 * Le forward est réalisé lorsque this est un prédécesseur naturel de msg issu de la même house.
	 * @see Msg.adjust
	 *
	 * Doit être implémenté si isBodyMutator==true.
	 */
	forward(msg: MsgOt): void {}

	/**
	 * TODO doc concept de cross
	 *
	 * Doit être implémenté si isBodyMutator==true.
	 *
	 * @param msg Msg sur lequel appliquer les transformations relatives à la traversée de this.
	 * @param invertOfOtId Id du MsgOt dont ce message est l'inversion (undo ou redo).
	 * @absrtact
	 */
	cross(msg: MsgOt, invertOfOtId: OtId): void {}

	/**
	 * Indique au message qu'il entre dans un processus de migration (synchro OT ou cross undo/redo) ou qu'il en sort.
	 * Entrée en migration : les msg contenant leur propre chaine de msg doivent se placer en mode rewind.
	 * Sortie de migration : Suppression des données temporaires relatives au processus de migration (cross) et forward des msg inclus,
	 *        voire optimisation du msg (fusion des chaines de msg).
	 * ATTENTION, cette méthode doit être idempotente : seul le premier appel à inMigration(b) doit avoir un effet,
	 * les autres appels successifs avec la même valeur b DOIVENT être sans effet.
	 *
	 * @param inMigr true quand on entre en migration, false, quand on en sort.
	 */
	inMigration(inMigr: boolean) {}

	/**
	 * Suite à un ajustement, message n'ayant plus aucun effet.
	 * @return {boolean}
	 */
	abstract get killed(): boolean;

	/**
	 * Supprime tout effet pour ce msg.
	 */
	abstract kill(): void;


	/**
	 * Retourne un nouveau message réalisant l'opération inverse.
	 * Au delà de le feature undo/redo, le mécanisme OT async implémenté impose que tout message Ot
	 * puisse être inversé. Ne pas oublier d'appeler copyStableMetasFrom()
	 */
	abstract invert(): MsgOt;

	/**
	 * Recopie les metas "stables" à partir d'un autre message.
	 * Une meta "stable" est préservée lorsqu'un message de type undo/redo est créé à partir de son msg d'origine.
	 * Une meta "stable" commence par un '$'.
	 */
	copyStableMetasFrom(other: Msg): this {
		if (other.metas) for (const k in other.metas) {
			if (k.charAt(0) === '$') this.setMeta(k, other.metas[k]);
		}
		return this;
	}

	/**
	 * Doit retourner true pour que ce Msg soit intégrable dans la chaine des actions de undo/redo.
	 * Si false est retourné, il sera imposssible de remonter au delà de ce point.
	 * Exemple envisagé: un msg de reset complet du contenu issu d'une migration : on ne veut pas
	 * permettre d'annuler la migration par undo.
	 */
	get isUndoable(): boolean {return true}

	/**
	 * Ce message peut-il potentiellement être fusionné avec un futur autre msg (encore inconnu à ce stade).
	 */
	get isMergeable(): boolean {return true}

	/**
	 * Fusion d'un Msg avec son message suivant. Retourne null si la fusion est impossible.
	 *
	 * @param nextMsg Msg suivant avec lequel tenter une fusion
	 * @returns Msg issu du merge. Peut-être this ou nextMsg ou null si le merge est impossible.
	 */
	mergeWith(nextMsg: MsgOt): MsgOt {return null}

	/** Assertion : valide la cohérence de ce msg par rapport au contenu courant de la house. */
	assertMsgValidity(house: HouseOt) {}

	toJson(json: any) {
		super.toJson(json);
		json.prg = this.prg;
		json.slvId = this.slvId;
		//json.otId = this.otId;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.prg = json.prg;
		this.slvId = json.slvId;
		//this.otId = json.otId;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.prg = o.prg;
		this.slvId = o.slvId;
		if (purpose === 'hist') this.doorId = o.doorId;
	}

	setHouseId(house: House): this {
		this.houseId = house.id;
		this.slvId = (house as HouseOt).slaveId;
		return this;
	}

}

function isMsgOt(msg: Msg): msg is MsgOt { return msg && ("otId" in msg)}

/** Msg qui implémente son propre processus de mise à jour */
export interface IMsgUpdater extends Msg {
	applyUpdates(state: State): void
}

export function isMsgUpdater(msg: Msg): msg is IMsgUpdater { return msg && ("applyUpdates" in msg)}


/**
 * Regroupement ordonné d'un ensemble de messages.
 * Permet de gérer un principe de transaction multi-msg et du principe
 * de décomposition d'un message en messages plus primitifs.
 */
class ListMsgOt extends MsgOt implements IMsgUpdater {
	static readonly type: string = "listOt";

	msgs: Array<MsgOt>;

	protected inMigr = false;

	initHouse(slaveId: number, doorId?: number): this {
		this.slvId = slaveId;
		if (doorId) this.doorId = doorId;
		return this;
	}

	initList(msgs: Array<MsgOt>, inMigr?: boolean): this {
		this.msgs = msgs;
		if (inMigr) this.inMigr = true;
		return this;
	}

	/**
	 * Initialise avec une liste de msg qui sont tous définis par rapport à l'état courant du document.
	 * Les messages vont donc être préalablement ajustés pour pouvoir être enchainés.
	 */
	initConcurrentList(msgs: Array<MsgOt>, inMigr?: boolean): this {
		for (let i = 0; i < msgs.length; i++) {
			const m = msgs[i];
			for (let k = 0; k < i; k++) msgs[k].adjust(m);
		}
		this.msgs = msgs;
		if (inMigr) this.inMigr = true;
		return this;
	}

	setDoorId(doorId: number): this {
		this.doorId = doorId;
		this.msgs.forEach(m => {
			m.setDoorId(doorId);
		});
		return this;
	}

	setHouseId(house: House): this {
		super.setHouseId(house);
		this.msgs.forEach(m => {
			m.setHouseId(house);
		});
		return this;
	}

	get isBodyMutator() {return this.msgs.some(m => m.isBodyMutator)}

	get isPrimary() {return this.msgs.every(m => m.isPrimary)}

	get isMergeable() {return false}

	decompose() {
		return new (this.constructor as any)().initList(this.msgs.map(m => m.decompose()), this.inMigr);
	}

	adjust(msg: MsgOt) {
		this.msgs.forEach(m => {m.adjust(msg)});
	}

	rewind(msg: MsgOt) {
		for (let i = this.msgs.length - 1; i >= 0; i--) this.msgs[i].rewind(msg);
	}

	forward(msg: MsgOt) {
		for (let i = 0, s = this.msgs.length; i < s; i++) this.msgs[i].forward(msg);
	}

	cross(msg: MsgOt, invertOfOtId: OtId) {
		//this.msgs.forEach((m, i) => {m.cross(msg, invertOfOtId)});
		const s = this.msgs.length - 1;
		this.msgs.forEach((m, i) => {
			if (!m.otId) m.otId = this.otId + "_" + i;
			m.cross(msg, invertOfOtId != null ? invertOfOtId + "_" + (s - i) : null);
		});
	}

	inMigration(inMigr: boolean) {
		if (inMigr) {
			if (this.inMigr) return; //déjà en migration
			this.inMigr = true;
			if (HouseOt.DEBUG) console.group("Enter in migr : " + this);
			for (let i = this.msgs.length - 1; i >= 0; i--) {
				const m = this.msgs[i];
				if (HouseOt.DEBUG) if (i > 0) console.log("Will rewind : " + m);
				m.inMigration(true);
				for (let k = i - 1; k >= 0; k--) {
					this.msgs[k].rewind(m);
					if (HouseOt.DEBUG) console.log("  Rewinded via " + this.msgs[k] + " : " + m);
				}
			}
			if (HouseOt.DEBUG) console.groupEnd();
		} else {
			if (!this.inMigr) return; //déjà sorti de la migration
			this.inMigr = false;
			if (HouseOt.DEBUG) console.group("Leave migr : " + this);
			for (let i = 0, s = this.msgs.length; i < s; i++) {
				const m = this.msgs[i];
				if (HouseOt.DEBUG) if (i < s - 1) console.log("Forward via " + m);
				m.inMigration(false);
				for (let k = i + 1; k < s; k++) {
					m.forward(this.msgs[k]);
					if (HouseOt.DEBUG) console.log("  Forwarded : " + this.msgs[k]);
				}
			}
			if (HouseOt.DEBUG) console.groupEnd();
		}
	}

	get killed() {
		return this.msgs.every(m => m.killed)
	}

	kill() {
		this.msgs.forEach(m => {m.kill()});
	}

	get isUndoable() {
		return this.msgs.every(m => m.isUndoable)
	}

	invert() {
		if (HouseOt.ASSERT) if (this.inMigr) throw Error("ListMsgOt.invert() called while this msg is in migration");
		const newMsgs = [];
		for (let i = this.msgs.length - 1; i >= 0; i--) newMsgs.push(this.msgs[i].invert());
		return new (this.constructor as any)().initList(newMsgs).copyStableMetasFrom(this);
	}

	applyUpdates(state: State) {
		this.msgs.forEach(m => {state.update(m)});
	}

	toJson(json: any) {
		super.toJson(json);
		json.inMigr = this.inMigr;
		json.list = this.msgs.map(m => m.asJson());
	}

	toStruct(struct: any) {
		super.toStruct(struct);
		struct.inMigr = this.inMigr;
		struct.list = this.msgs.map(m => m.asStruct());
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.inMigr = json.inMigr;
		this.msgs = json.list.map((m: any) => factoryReg.newFromJson(m));
	}

	fromStruct(struct: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromStruct(struct, factoryReg);
		this.inMigr = struct.inMigr;
		this.msgs = struct.list.map((m: any) => factoryReg.newFromStruct(m));
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.inMigr = o.inMigr;
		this.msgs = o.msgs.map(m => m.clone(purpose));
	}

	toString() {
		//pour debug, on publie aussi les props non sérialisées
		const json = this.asJson();
		if (this.otId) json.otId = this.otId;
		if (this.inMigr) json.inMigr = true;
		return JSON.stringify(json);
	}
}

House.MSG_FACTORYREG.register(ListMsgOt);


export class UndirtyMsgOt<P extends Jsonisable> extends Msg {
	static readonly type: string = "undirty";

	prg: number
	props: P;

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.prg = json.prg;
		this.props = json.props;
	}

	toJson(json: any) {
		super.toJson(json)
		json.prg = this.prg
		json.props = this.props;
	}
}

House.MSG_FACTORYREG.register(UndirtyMsgOt);

/** Demande d'enregistrement à la house master par une house slave. */
export class AskForSaveReq<PR extends Jsonisable> extends Msg implements IRequest<SaveRep> {
	static readonly type: string = "askForSave";
}

House.MSG_FACTORYREG.register(AskForSaveReq);

/** Réponse de la house master à la house slave suite à un AskForSaveReq. */
export class SaveRep extends ReplyMsg {
	static readonly type: string = "saveRep";
}

House.MSG_FACTORYREG.register(SaveRep);

/** Meta d'un msg indiquant qu'un autoCleanup (suppresion des balises inconnues, non autorisées) ne devrait pas être effectué lors de la correction des messages. */
export const MSGMETA_noCleanup = "noCleanup";

export {OHouseOtConfig, HouseOt, MsgOt, isMsgOt, ListMsgOt, OtId, IMsgFilter}
